import { CompanyFormikSelectFormField } from 'components/molecules/SelectFormField/CompanySelectFormField/CompanySelectFormField';
import { Formik, useField } from 'formik';
import {
  TechnicalSkillFindManyPaginatedDocument,
  useTechnicalSkillCreateOneMutation,
} from 'generated/graphql';
import {
  AddTechnicalSkillFormValues,
  dataTestIds,
  initialValues,
  validationShema,
} from 'views/StaticContent/TechnicalSkills/AddTechnicalSkillDrawer/AddTechnicalSkillDrawer.helpers';
import {
  Button,
  Drawer,
  FormikTextInputField,
  InlineNotification,
  NotificationType,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './AddTechnicalSkillDrawer.module.scss';

type AddTechnicalSkillDrawerProps = {
  open: boolean;
  onClose: () => void;
};

export function AddTechnicalSkillDrawer({
  open,
  onClose,
}: AddTechnicalSkillDrawerProps) {
  return (
    <Drawer open={open} title="Add Technical Skill" onClose={onClose}>
      <DrawerContent onClose={onClose} />
    </Drawer>
  );
}

type DrawerContentProps = {
  onClose: () => void;
};

function DrawerContent({ onClose }: DrawerContentProps) {
  const { handleMsgType } = useNotification();

  const [createNewTechnicalSkillMutation, { loading: createLoading }] =
    useTechnicalSkillCreateOneMutation({
      onError: error =>
        handleMsgType({
          type: TNotification.error,
          message: error.message,
          time: 1500,
          dismissable: true,
        }),
      onCompleted: data => {
        handleMsgType({
          type: TNotification.success,
          message: `Technical skill ${data.TechnicalSkillCreateOne.name} created!`,
          time: 1500,
          dismissable: true,
        });
        onClose();
      },
      refetchQueries: [TechnicalSkillFindManyPaginatedDocument],
    });

  const handleSubmit = (formValues: AddTechnicalSkillFormValues) => {
    createNewTechnicalSkillMutation({
      variables: {
        name: formValues.name,
        description: formValues.description,
        companyId:
          formValues.ownership.value !== '-1'
            ? Number(formValues.ownership.value)
            : null,
      },
    });
  };

  return (
    <Formik<AddTechnicalSkillFormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationShema}
    >
      {({ submitForm }) => (
        <form onSubmit={e => e.preventDefault()} data-testid={dataTestIds.form}>
          <div className={styles.buttonContainer}>
            <Button
              className={styles.button}
              onClick={submitForm}
              variant="primary"
              size="large"
              data-testid={dataTestIds.save}
              disabled={createLoading}
            >
              Save
            </Button>
            <Button
              className={styles.button}
              onClick={() => onClose()}
              variant="secondary"
              size="large"
              data-testid={dataTestIds.cancel}
              disabled={createLoading}
            >
              Cancel
            </Button>
          </div>
          <div>
            <FormikTextInputField
              id="name"
              label="Name"
              data-role="technical-skill-name-input"
              data-testid={dataTestIds.nameInput}
              useFormikField={useField}
              placeholder="Name"
            />
            <FormikTextInputField
              id="description"
              label="Description"
              data-role="soft-skill-description-input"
              data-testid={dataTestIds.descriptionInput}
              useFormikField={useField}
              placeholder="Description"
            />
            <div className={styles.fieldWithWarning}>
              <CompanyFormikSelectFormField
                id="ownership"
                label="Ownership"
                placeholder="Ownership"
                hasClearIndicator={false}
                maxMenuHeight={240}
                hasGlobal
                selectProps={{
                  'data-testid': dataTestIds.ownershipSelect,
                }}
              />
              <InlineNotification
                notificationType={NotificationType.WARNING}
                className={styles.inlineWarning}
              >
                Ownership cannot be changed after creation!
              </InlineNotification>
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
}
